import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, Typography, Alert } from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { UserRole, Permission, getDefaultHomePath } from '../../types/auth';

// Componente para proteger rutas por rol
interface ProtectedByRoleProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
  fallback?: React.ReactNode;
  /** Si se omite, se usa la primera ruta permitida para el usuario. */
  redirectTo?: string;
}

export const ProtectedByRole: React.FC<ProtectedByRoleProps> = ({ 
  children, 
  allowedRoles, 
  fallback,
  redirectTo
}) => {
  const { hasAnyRole, isLoggedIn, user } = useAuth();

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  if (!hasAnyRole(allowedRoles)) {
    if (fallback) {
      return <>{fallback}</>;
    }
    
    const target = redirectTo ?? (user ? getDefaultHomePath(user) : '/login');
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
};

// Componente para proteger rutas por permiso
interface ProtectedByPermissionProps {
  children: React.ReactNode;
  requiredPermission: Permission;
  fallback?: React.ReactNode;
  /** Si se omite, se usa la primera ruta permitida para el usuario. */
  redirectTo?: string;
}

export const ProtectedByPermission: React.FC<ProtectedByPermissionProps> = ({ 
  children, 
  requiredPermission, 
  fallback,
  redirectTo
}) => {
  const { hasPermission, isLoggedIn, user } = useAuth();

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }

  if (!hasPermission(requiredPermission)) {
    if (fallback) {
      return <>{fallback}</>;
    }
    
    const target = redirectTo ?? (user ? getDefaultHomePath(user) : '/login');
    return <Navigate to={target} replace />;
  }

  return <>{children}</>;
};

// Componente para mostrar/ocultar elementos del UI por rol
interface ShowForRolesProps {
  children: React.ReactNode;
  roles: UserRole[];
  fallback?: React.ReactNode;
}

export const ShowForRoles: React.FC<ShowForRolesProps> = ({ 
  children, 
  roles, 
  fallback = null 
}) => {
  const { hasAnyRole } = useAuth();

  if (hasAnyRole(roles)) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};

// Componente para mostrar/ocultar elementos del UI por permiso
interface ShowForPermissionProps {
  children: React.ReactNode;
  permission: Permission;
  fallback?: React.ReactNode;
}

export const ShowForPermission: React.FC<ShowForPermissionProps> = ({ 
  children, 
  permission, 
  fallback = null 
}) => {
  const { hasPermission } = useAuth();

  if (hasPermission(permission)) {
    return <>{children}</>;
  }

  return <>{fallback}</>;
};